import { format } from "date-fns";
import { useListAndSearchAllInvestments } from "@rentablo/api/apiv1";
import { getDateInterval } from "@helpers";
import { usePublicOrPrivatePerformance } from "@api/v1/hooks/performance/usePublicOrPrivatePerformance";

export interface InvestmentsChartDataStatistic {
  portfolioValue: number;
  valuationGain: number;
  cashBalance: number;
  realizedGain: number;
  dividendGain: number;
  changeToday: number;
}

export const useInvestmentsChartDataTotal = (
  accountIds: number[],
  startDate: Date,
  endDate: Date
) => {
  const interval = getDateInterval(startDate, endDate);
  const isEnabled =
    Boolean(startDate && endDate) && Boolean(accountIds.length > 0);

  const {
    data: performanceData,
    currency,
    isFetching,
    isFetched,
  } = usePublicOrPrivatePerformance(
    {
      accountIds,
      startDate: format(startDate!, "yyyy-MM-dd"),
      endDate: format(endDate!, "yyyy-MM-dd"),
      interval,
      cashFlowAndPerformanceStatisticsParamsList: [
        {
          includeCash: false,
          includeDividends: true,
        },
        {
          includeCash: false,
          includeDividends: false,
        },
        {
          includeCash: true,
          includeDividends: true,
        },
      ],
    },
    { enabled: isEnabled }
  );

  const {
    data,
    isFetching: isInvestmentsLoading,
    isFetched: isInvestmentsFetched,
  } = useListAndSearchAllInvestments({
    id: accountIds,
  });

  const statistics = performanceData[0];
  const cashStatistics = performanceData[2];
  const aggregatedStatistics = performanceData[0]?.aggregatedGainLossStatistics;

  const twrorResult =
    statistics?.twrorResults[statistics.twrorResults.length - 1];
  const cashTwrorResult =
    cashStatistics?.twrorResults[statistics.twrorResults.length - 1];

  const portfolioValue = cashTwrorResult?.currentMarketValue ?? 0;
  const cashBalance = data?.cashBalance || (twrorResult?.cashBalance ?? 0);
  const valuationGain = twrorResult?.valuationGainAbs ?? 0;
  const dividendGain = aggregatedStatistics?.grossDividendGain ?? 0;
  const realizedGain = aggregatedStatistics?.grossTradingGain ?? 0;
  const changeToday = statistics?.changeLast?.performance ?? 0;

  const statistic =
    isEnabled && !(isFetching || isInvestmentsLoading)
      ? {
          portfolioValue,
          cashBalance,
          valuationGain,
          dividendGain,
          realizedGain,
          changeToday,
        }
      : undefined;

  return {
    statistic,
    currency,
    isFetching: isFetching || isInvestmentsLoading,
    isFetched: isFetched && isInvestmentsFetched,
  };
};
